'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import classNames from 'classnames';
import I18n from 'I18n';
import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import UIClickable from '../button/UIClickable';
import { FieldsetContextConsumer } from '../context/FieldsetContext';
import { callIfPossible } from '../core/Functions';
import SyntheticEvent from '../core/SyntheticEvent';
import Controllable from '../decorators/Controllable';
import ShareInput from '../decorators/ShareInput';
import UIIcon from '../icon/UIIcon';
import UITextInput from '../input/UITextInput';
import UITooltip from '../tooltip/UITooltip';
import lazyEval from '../utils/lazyEval';
import { getTextWidth } from '../utils/MeasureText';
import { hidden } from '../utils/propTypes/decorators';
import deprecated from '../utils/propTypes/deprecated';
import { propTypeForSizes, toShorthandSize } from '../utils/propTypes/tshirtSize';

const getMinimumSearchMessage = (minimumSearchCount, value, getValidationMessage) => {
  //This is deprecating minimumSearchCount, so if an getValidationMessage is present it will ignore minimumSearchCount
  if (getValidationMessage) {
    return getValidationMessage(value);
  }

  const valueLength = value ? value.length : 0;
  const charsNeeded = minimumSearchCount - valueLength;

  if (valueLength && charsNeeded > 0) {
    return I18n.text('salesUI.UISearchInput.minimumSearchMessage', {
      count: charsNeeded
    });
  }

  return null;
};

class UISearchInput extends PureComponent {
  constructor(props) {
    super(props);

    this.handleKeyDown = evt => {
      const {
        onKeyDown,
        readOnly
      } = this.props;
      if (readOnly) return;

      if (evt.key === 'Escape') {
        this.triggerClear();
      }

      callIfPossible(onKeyDown, evt);
    };

    this.triggerClear = () => {
      const {
        inputRef,
        onChange
      } = this.props;
      onChange(SyntheticEvent(''));
      inputRef.current.focus();
    };

    this.state = {
      minimumSearchMessageOverflows: false
    };
  }

  componentDidMount() {
    const {
      getValidationMessage,
      minimumSearchCount
    } = this.props;
    if (getValidationMessage || minimumSearchCount > 0) this.computeMinimumSearchMessageOverflow();
  }

  componentDidUpdate() {
    const {
      getValidationMessage,
      minimumSearchCount
    } = this.props;
    if (getValidationMessage || minimumSearchCount > 0) this.computeMinimumSearchMessageOverflow();
  }

  computeMinimumSearchMessageOverflow() {
    // Determine whether there's enough space to show the minimum search message inline (#2869)
    const {
      inputRef
    } = this.props;
    if (!this._wrapperEl || !this._iconEl || !this._helpTextEl) return;
    const wrapperWidth = this._wrapperEl.offsetWidth - this._iconEl.offsetWidth;
    const searchMessageWidth = this._helpTextEl.offsetWidth;
    const textWidth = getTextWidth(inputRef.current);
    const minimumSearchMessageOverflows = searchMessageWidth > wrapperWidth - textWidth;

    if (minimumSearchMessageOverflows !== this.state.minimumSearchMessageOverflows) {
      this.setState({
        minimumSearchMessageOverflows
      });
    }
  }

  render() {
    const _this$props = this.props,
          {
      className,
      clearable,
      icon,
      inputClassName,
      getValidationMessage,
      minimumSearchCount,
      placeholder,
      readOnly,
      size,
      value
    } = _this$props,
          rest = _objectWithoutPropertiesLoose(_this$props, ["className", "clearable", "icon", "inputClassName", "getValidationMessage", "minimumSearchCount", "placeholder", "readOnly", "size", "value"]);

    const {
      minimumSearchMessageOverflows
    } = this.state;
    return /*#__PURE__*/_jsx(FieldsetContextConsumer, {
      children: fieldsetContext => {
        const shorthandSize = toShorthandSize(size);
        const computedClassName = classNames('private-search-control', className, shorthandSize === 'xl' && 'private-search-control--xl');
        const showClearButton = clearable && value && !readOnly;
        const minimumSearchMessage = getMinimumSearchMessage(minimumSearchCount, value, getValidationMessage);
        const renderedMinimumSearchMessage = minimumSearchMessage ? /*#__PURE__*/_jsx("span", {
          "aria-atomic": false,
          "aria-live": "polite",
          className: 'private-search-control__help-text' + (minimumSearchMessageOverflows ? " private-search-control__help-text--hidden" : ""),
          ref: ref => {
            this._helpTextEl = ref;
          },
          children: minimumSearchMessage
        }) : null;
        const renderedSearchIcon = icon && !readOnly ? /*#__PURE__*/_jsx("span", {
          ref: ref => {
            this._iconEl = ref;
          },
          children: /*#__PURE__*/_jsx(UIIcon, {
            className: classNames('private-search-control__icon', showClearButton && 'private-search-control__icon--hidden', fieldsetContext.size === 'small' && 'private-search-control__icon--small'),
            name: "search"
          })
        }) : null;
        const renderedClearButton = showClearButton ? /*#__PURE__*/_jsx(UIClickable, {
          className: 'private-search-control__clear-button' + (fieldsetContext.size === 'small' ? " private-search-control__clear-button--small" : ""),
          onClick: this.triggerClear,
          tabIndex: -1,
          children: /*#__PURE__*/_jsx(UIIcon, {
            name: "remove"
          })
        }) : null;
        return /*#__PURE__*/_jsx(UITooltip, {
          open: !!(minimumSearchMessage && minimumSearchMessageOverflows),
          title: minimumSearchMessage,
          children: /*#__PURE__*/_jsxs("div", {
            className: computedClassName,
            ref: ref => {
              this._wrapperEl = ref;
            },
            children: [/*#__PURE__*/_jsx(UITextInput, Object.assign({}, rest, {
              className: classNames('private-search-control__input', inputClassName),
              onKeyDown: this.handleKeyDown,
              placeholder: lazyEval(placeholder),
              readOnly: readOnly,
              value: value
            })), /*#__PURE__*/_jsxs("span", {
              className: "private-search-control__foreground",
              children: [renderedMinimumSearchMessage, renderedSearchIcon]
            }), renderedClearButton]
          })
        });
      }
    });
  }

}

UISearchInput.propTypes = Object.assign({}, UITextInput.propTypes, {
  clearable: PropTypes.bool.isRequired,
  icon: PropTypes.bool.isRequired,
  inputClassName: PropTypes.string,
  getValidationMessage: PropTypes.func,
  minimumSearchCount: deprecated(PropTypes.number, {
    message: `UISearchInput: The \`minimumSearchCount\` prop is deprecated. Use \`getValidationMessage\` instead.`,
    key: 'UISearchInput-minimumSearchCount'
  }, 0),
  size: PropTypes.oneOfType([propTypeForSizes(['xl']), PropTypes.oneOf(['default'])]).isRequired,
  type: hidden(UITextInput.propTypes.type)
});
UISearchInput.defaultProps = Object.assign({}, UITextInput.defaultProps, {
  clearable: true,
  icon: true,
  getValidationMessage: null,
  minimumSearchCount: 0,
  placeholder: () => I18n.text('salesUI.UISearchInput.placeholder'),
  size: 'default',
  type: 'search',
  value: ''
});
UISearchInput.displayName = 'UISearchInput';
export default ShareInput(Controllable(UISearchInput));